import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, AttachFile, CheckCircleOutline, Close, InsertDriveFile, PictureAsPdf, Refresh, SmartToy, SupportAgent, Send } from '@mui/icons-material';
import { format } from 'date-fns';

import { useSupportChatNotifications } from '../../context/SupportChatNotificationContext';
import { API_BASE_URL } from '../../services/api';
import supportChatService from '../../services/supportChatService';
import { hasEndpointAccess } from '../../utils/rbac';


const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch (error) {
    return value;
  }
};


const getDisplayName = (user) => {
  if (!user) {
    return 'Unassigned';
  }
  return user.fullname || user.username || user.email || user.phone || `User ${user.id}`;
};

const formatSupportMode = (mode) => {
  if (!mode) {
    return '-';
  }
  return mode.replace(/_/g, ' ').toUpperCase();
};

const getSupportModeColor = (mode) => {
  if (mode === 'live_agent') return 'success';
  if (mode === 'waiting_agent') return 'warning';
  return 'info';
};

const RATING_REQUEST_MESSAGE =
  "If you’re satisfied with the support, please rate this chat so we can improve our service.";


const TYPING_STOP_DELAY_MS = 1600;
const REMOTE_TYPING_TIMEOUT_MS = 4500;

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const ALLOWED_ATTACHMENT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];

const getAttachmentExtension = (name = '') => {
  if (!name || !name.includes('.')) return '';
  return name.split('.').pop().toLowerCase();
};

const isValidAttachment = (file) => {
  const extension = getAttachmentExtension(file?.name);
  const type = (file?.type || '').toLowerCase();
  return ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)
    && (!type || ALLOWED_ATTACHMENT_TYPES.includes(type));
};

const getAbsoluteAttachmentUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const parsed = new URL(API_BASE_URL);
  const origin = `${parsed.protocol}//${parsed.host}`;
  return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
};

const mergeIncomingMessage = (currentMessages, message) => {
  const exists = currentMessages.some((item) => item.id === message.id);
  if (exists) {
    return currentMessages;
  }
  return [...currentMessages, message];
};


export default function SupportChatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [reply, setReply] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [retentionHold, setRetentionHold] = useState(false);
  const [retentionHoldReason, setRetentionHoldReason] = useState('');
  const [savingRetention, setSavingRetention] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [userTyping, setUserTyping] = useState(false);
  const [markingNotificationsRead, setMarkingNotificationsRead] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const remoteTypingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(false);
  const {
    lastSupportEvent,
    markSessionNotificationsRead,
    socketConnected: notificationSocketConnected,
  } = useSupportChatNotifications();

  const canManageRetention = useMemo(
    () => hasEndpointAccess(user, 'admin-support-chat-session-retention-hold', 'POST'),
    [user]
  );
  const canSendMessage = useMemo(
    () => hasEndpointAccess(user, 'admin-support-chat-session-messages', 'POST'),
    [user]
  );

  const fetchSession = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await supportChatService.getSession(id);
      setSession(data);
    } catch (err) {
      console.error('Failed to load support chat session', err);
      setError(err?.response?.data?.detail || 'Failed to load support chat session.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (!session) {
      return;
    }
    setRetentionHold(!!session.retention_hold);
    setRetentionHoldReason(session.retention_hold_reason || '');
  }, [session]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    let active = true;

    const markNotificationsRead = async () => {
      try {
        setMarkingNotificationsRead(true);
        const data = await markSessionNotificationsRead(id);
        if (!active) {
          return;
        }
        setSession((prev) => (
          prev
            ? {
                ...prev,
                unread_notification_count: 0,
              }
            : prev
        ));
        return data;
      } catch (err) {
        console.error('Failed to mark support chat notifications as read', err);
        return null;
      } finally {
        if (active) {
          setMarkingNotificationsRead(false);
        }
      }
    };

    markNotificationsRead();

    return () => {
      active = false;
    };
  }, [id, markSessionNotificationsRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages]);

  const sendTypingState = useCallback((isTyping) => {
    if (lastTypingSentRef.current === isTyping) {
      return;
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: 'typing', is_typing: isTyping }));
      lastTypingSentRef.current = isTyping;
    }
  }, []);

  const handleReplyChange = useCallback((event) => {
    const value = event.target.value;
    setReply(value);

    if (submitting || session?.status === 'closed') {
      return;
    }

    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }

    const isTyping = value.trim().length > 0;
    sendTypingState(isTyping);

    if (isTyping) {
      typingStopTimerRef.current = window.setTimeout(() => {
        sendTypingState(false);
        typingStopTimerRef.current = null;
      }, TYPING_STOP_DELAY_MS);
    }
  }, [sendTypingState, session?.status, submitting]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    let active = true;
    let reconnectTimer = null;

    const connectSocket = () => {
      if (!active) {
        return;
      }

      try {
        const socket = new WebSocket(supportChatService.buildSessionWebSocketUrl(id));
        socketRef.current = socket;

        socket.onopen = () => {
          if (!active) {
            return;
          }
          setSocketConnected(true);
        };

        socket.onmessage = (event) => {
          if (!active) {
            return;
          }

          try {
            const payload = JSON.parse(event.data);
            if (payload.type === 'chat.typing') {
              if (payload.sender_type !== 'user') {
                return;
              }

              if (remoteTypingTimerRef.current) {
                window.clearTimeout(remoteTypingTimerRef.current);
                remoteTypingTimerRef.current = null;
              }

              setUserTyping(Boolean(payload.is_typing));

              if (payload.is_typing) {
                remoteTypingTimerRef.current = window.setTimeout(() => {
                  setUserTyping(false);
                  remoteTypingTimerRef.current = null;
                }, REMOTE_TYPING_TIMEOUT_MS);
              }
            } else if (payload.type === 'chat.message.created' && payload.message) {
              if (payload.message.sender_type === 'user') {
                setUserTyping(false);
              }
              setSession((prev) => {
                if (!prev) {
                  return prev;
                }

                return {
                  ...prev,
                  ...payload.session,
                  messages: mergeIncomingMessage(prev.messages || [], payload.message),
                };
              });
            } else if (
              payload.type === 'chat.session.updated' ||
              payload.type === 'chat.session.closed'
            ) {
              setSession((prev) => (
                prev ? { ...prev, ...payload.session, messages: prev.messages || [] } : prev
              ));
            }
          } catch (parseError) {
            console.error('Failed to parse support chat websocket event', parseError);
          }
        };

        socket.onclose = () => {
          if (!active) {
            return;
          }
          setSocketConnected(false);
          lastTypingSentRef.current = false;
          reconnectTimer = window.setTimeout(connectSocket, 5000);
        };

        socket.onerror = () => {
          setSocketConnected(false);
        };
      } catch (socketError) {
        console.error('Support chat websocket error', socketError);
        setSocketConnected(false);
      }
    };

    connectSocket();

    return () => {
      active = false;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      if (typingStopTimerRef.current) {
        window.clearTimeout(typingStopTimerRef.current);
      }
      if (remoteTypingTimerRef.current) {
        window.clearTimeout(remoteTypingTimerRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [id]);

  useEffect(() => {
    if (socketConnected) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchSession();
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [fetchSession, socketConnected]);

  useEffect(() => {
    if (!lastSupportEvent) {
      return undefined;
    }

    const notification = lastSupportEvent.notification;
    if (notification?.session_id && notification.session_id !== id) {
      return undefined;
    }

    if (
      lastSupportEvent.type === 'support.notification.created'
      || lastSupportEvent.type === 'support.notification.read'
    ) {
      setSession((prev) => (
        prev
          ? {
              ...prev,
              unread_notification_count:
                lastSupportEvent.type === 'support.notification.read'
                  ? 0
                  : Math.max(0, (prev.unread_notification_count || 0) + 1),
            }
          : prev
      ));
    }

    if (
      lastSupportEvent.type === 'support.notification.created'
      && notification?.metadata?.session_id === id
      && !socketConnected
    ) {
      const timeoutId = window.setTimeout(() => {
        fetchSession();
      }, 800);
      return () => window.clearTimeout(timeoutId);
    }

    return undefined;
  }, [fetchSession, id, lastSupportEvent, socketConnected]);

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isValidAttachment(file)) {
      setError('Only JPG, PNG and PDF attachments are supported.');
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      setError('Attachment must be 10MB or smaller.');
      return;
    }

    setSelectedAttachment(file);
    setError('');
  };

  const sendReply = async () => {
    const content = reply.trim();
    if (!content && !selectedAttachment) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      sendTypingState(false);
      await supportChatService.sendMessage(id, {
        content,
        message_type: selectedAttachment ? 'attachment' : 'text',
        attachment: selectedAttachment,
      });
      setReply('');
      setSelectedAttachment(null);
      if (!socketConnected) {
        await fetchSession();
      }
    } catch (err) {
      console.error('Failed to send support chat reply', err);
      setError(err?.response?.data?.detail || 'Failed to send reply.');
    } finally {
      setSubmitting(false);
    }
  };

  const requestRating = async () => {
    try {
      setSubmitting(true);
      setError('');
      await supportChatService.sendMessage(id, { content: RATING_REQUEST_MESSAGE, message_type: 'text' });
      if (!socketConnected) {
        await fetchSession();
      }
    } catch (err) {
      console.error('Failed to request support agent rating', err);
      setError(err?.response?.data?.detail || 'Failed to request rating.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeConversation = async () => {
    try {
      setSubmitting(true);
      setError('');
      const data = await supportChatService.closeSession(id);
      setSession(data);
    } catch (err) {
      console.error('Failed to close support chat session', err);
      setError(err?.response?.data?.detail || 'Failed to close session.');
    } finally {
      setSubmitting(false);
    }
  };

  const activateConversation = async () => {
    try {
      setSubmitting(true);
      setError('');
      const data = await supportChatService.updateSession(id, { status: 'active' });
      setSession(data);
    } catch (err) {
      console.error('Failed to update support chat session', err);
      setError(err?.response?.data?.detail || 'Failed to mark session active.');
    } finally {
      setSubmitting(false);
    }
  };

  const takeOverChat = async () => {
    try {
      setSubmitting(true);
      setError('');
      const data = await supportChatService.takeOverSession(id);
      setSession(data);
    } catch (err) {
      console.error('Failed to take over support chat session', err);
      setError(err?.response?.data?.detail || 'Failed to take over chat.');
    } finally {
      setSubmitting(false);
    }
  };

  const messageList = useMemo(() => session?.messages || [], [session]);

  const retentionChip = useMemo(() => {
    if (!session) return null;
    if (session.retention_hold) return { label: 'HOLD', color: 'error' };
    if (session.purged_at) return { label: 'PURGED', color: 'default' };
    if (session.is_archived) return { label: 'ARCHIVED', color: 'warning' };
    return { label: 'ACTIVE', color: 'success' };
  }, [session]);

  const saveRetentionHold = useCallback(async () => {
    if (!session) {
      return;
    }
    try {
      setSavingRetention(true);
      setError('');
      const updated = await supportChatService.setRetentionHold(session.id, {
        retention_hold: retentionHold,
        retention_hold_reason: retentionHoldReason,
      });
      setSession(updated);
    } catch (err) {
      console.error('Failed to update retention hold', err);
      const data = err?.response?.data;
      const reasonError = Array.isArray(data?.retention_hold_reason) ? data.retention_hold_reason[0] : null;
      setError(reasonError || data?.detail || 'Failed to update retention hold.');
    } finally {
      setSavingRetention(false);
    }
  }, [retentionHold, retentionHoldReason, session]);

  if (loading) {
    return (
      <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Support chat session not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', px: { xs: 1, md: 2 }, py: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/support-chat')} sx={{ mb: 1, px: 0 }}>
            Back to sessions
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#DC143C' }}>
            Support Chat Session
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Session ID: {session.id}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={socketConnected ? 'Chat socket connected' : 'Chat polling fallback active'}
            color={socketConnected ? 'success' : 'warning'}
            icon={<CheckCircleOutline />}
          />
          <Chip
            label={notificationSocketConnected ? 'Alert socket connected' : 'Alert REST fallback active'}
            color={notificationSocketConnected ? 'success' : 'default'}
            size="small"
          />
          {markingNotificationsRead ? (
            <Chip label="Marking alerts read" color="default" size="small" />
          ) : null}
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchSession} disabled={submitting}>
            Refresh
          </Button>
          {(session.support_mode === 'live_agent' || session.assigned_agent) ? (
            <Button
              variant="outlined"
              onClick={requestRating}
              disabled={submitting || !canSendMessage}
            >
              Request Rating
            </Button>
          ) : null}
          {session.status !== 'closed' && session.support_mode !== 'live_agent' ? (
            <Button
              variant="contained"
              startIcon={<SupportAgent />}
              onClick={takeOverChat}
              disabled={submitting}
              sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            >
              Take Over Chat
            </Button>
          ) : null}
          {session.status !== 'active' && session.support_mode === 'live_agent' ? (
            <Button variant="outlined" onClick={activateConversation} disabled={submitting}>
              Mark Active
            </Button>
          ) : null}
          {session.status !== 'closed' ? (
            <Button variant="contained" color="error" onClick={closeConversation} disabled={submitting}>
              Close Session
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Card sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Session Details</Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">User</Typography>
                <Typography variant="body1">{getDisplayName(session.user)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Assigned Agent</Typography>
                <Typography variant="body1">{getDisplayName(session.assigned_agent)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={(session.status || '').toUpperCase()}
                    color={session.status === 'active' ? 'success' : session.status === 'closed' ? 'default' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Retention</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {retentionChip ? (
                    <Chip label={retentionChip.label} color={retentionChip.color} size="small" sx={{ fontWeight: 700 }} />
                  ) : null}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Support Mode</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={formatSupportMode(session.support_mode)}
                    color={getSupportModeColor(session.support_mode)}
                    size="small"
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Intent</Typography>
                <Typography variant="body2">
                  {session.intent ? session.intent.replace(/_/g, ' ') : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Unread Alerts</Typography>
                <Typography variant="body2">{session.unread_notification_count || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created At</Typography>
                <Typography variant="body2">{formatDateTime(session.created_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last Message At</Typography>
                <Typography variant="body2">{formatDateTime(session.last_message_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last Activity At</Typography>
                <Typography variant="body2">{formatDateTime(session.last_activity_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Last User Message At</Typography>
                <Typography variant="body2">{formatDateTime(session.last_user_message_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Inactivity Warning Sent</Typography>
                <Typography variant="body2">{formatDateTime(session.inactivity_warning_sent_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Auto Closed At</Typography>
                <Typography variant="body2">{formatDateTime(session.auto_closed_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Close Reason</Typography>
                <Typography variant="body2">{session.close_reason ? String(session.close_reason).replace(/_/g, ' ') : '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Archived At</Typography>
                <Typography variant="body2">{formatDateTime(session.archived_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Purged At</Typography>
                <Typography variant="body2">{formatDateTime(session.purged_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Closed At</Typography>
                <Typography variant="body2">{formatDateTime(session.closed_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Escalated At</Typography>
                <Typography variant="body2">{formatDateTime(session.escalated_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Escalation Reason</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {session.escalation_reason || '-'}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1.5 }}>Retention Hold</Typography>
            <Stack spacing={1.25}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={retentionHold ? 'contained' : 'outlined'}
                  color={retentionHold ? 'error' : 'inherit'}
                  onClick={() => setRetentionHold(true)}
                  disabled={!canManageRetention || savingRetention}
                >
                  Enable hold
                </Button>
                <Button
                  variant={!retentionHold ? 'contained' : 'outlined'}
                  onClick={() => setRetentionHold(false)}
                  disabled={!canManageRetention || savingRetention}
                >
                  Disable hold
                </Button>
              </Box>
              <TextField
                label="Reason"
                value={retentionHoldReason}
                onChange={(event) => setRetentionHoldReason(event.target.value)}
                size="small"
                multiline
                minRows={2}
                disabled={!canManageRetention || savingRetention || !retentionHold}
                placeholder={retentionHold ? 'e.g. dispute / payment / fraud / manual hold' : 'Enable hold to set a reason'}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={saveRetentionHold}
                disabled={!canManageRetention || savingRetention}
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  '&:hover': { backgroundColor: (theme) => theme.palette.primary.dark },
                }}
              >
                {savingRetention ? 'Saving...' : 'Save'}
              </Button>
              {!canManageRetention ? (
                <Typography variant="caption" color="text.secondary">
                  You do not have permission to update retention hold.
                </Typography>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 640 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Conversation</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                backgroundColor: '#fafafa',
                borderRadius: 2,
                px: { xs: 1.5, md: 2 },
                py: 2,
                mb: 2,
              }}
            >
              <Stack spacing={1.5}>
                {messageList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No messages yet.
                  </Typography>
                ) : (
                  messageList.map((message) => {
                    const isAgent = message.sender_type === 'agent';
                    const isBot = message.sender_type === 'bot';
                    const bubbleColor = isAgent ? '#DC143C' : isBot ? '#E8F4FD' : '#ffffff';
                    const textColor = isAgent ? '#ffffff' : '#111827';
                    const borderColor = isBot ? '#90CAF9' : '#E5E7EB';
                    const messageLabel = isBot ? 'Assistant' : isAgent ? 'Agent' : 'User';

                    return (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isAgent ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '80%',
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            backgroundColor: bubbleColor,
                            color: textColor,
                            border: `1px solid ${isAgent ? '#DC143C' : borderColor}`,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            {isBot ? <SmartToy sx={{ fontSize: 16 }} /> : null}
                            <Typography variant="caption" sx={{ opacity: isAgent ? 0.9 : 0.7, display: 'block' }}>
                              {message.sender_display_name || message.sender_type}
                            </Typography>
                            <Chip
                              label={messageLabel}
                              size="small"
                              color={isAgent ? 'error' : isBot ? 'info' : 'default'}
                              sx={{ height: 20 }}
                            />
                          </Stack>
                          {message.content ? (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {message.content}
                            </Typography>
                          ) : null}
                          {message.attachment_url ? (() => {
                            const attachmentUrl = getAbsoluteAttachmentUrl(message.attachment_url);
                            const attachmentName = message.attachment_name || 'Attachment';
                            const attachmentType = message.attachment_type || '';
                            const extension = (message.attachment_extension || getAttachmentExtension(attachmentName)).toLowerCase();
                            const isImage = attachmentType.startsWith('image/') || ['jpg', 'jpeg', 'png'].includes(extension);
                            const isPdf = attachmentType === 'application/pdf' || extension === 'pdf';

                            return (
                              <Box
                                component="a"
                                href={attachmentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'block',
                                  mt: message.content ? 1 : 0,
                                  color: 'inherit',
                                  textDecoration: 'none',
                                }}
                              >
                                {isImage ? (
                                  <Box
                                    component="img"
                                    src={attachmentUrl}
                                    alt={attachmentName}
                                    sx={{
                                      width: 220,
                                      maxWidth: '100%',
                                      height: 150,
                                      objectFit: 'cover',
                                      borderRadius: 1.5,
                                      border: `1px solid ${isAgent ? 'rgba(255,255,255,0.35)' : '#E5E7EB'}`,
                                    }}
                                  />
                                ) : (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{
                                      p: 1.25,
                                      borderRadius: 1.5,
                                      border: `1px solid ${isAgent ? 'rgba(255,255,255,0.35)' : '#E5E7EB'}`,
                                      backgroundColor: isAgent ? 'rgba(255,255,255,0.12)' : '#F9FAFB',
                                      minWidth: 220,
                                    }}
                                  >
                                    {isPdf ? <PictureAsPdf fontSize="small" /> : <InsertDriveFile fontSize="small" />}
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                        {attachmentName}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: isAgent ? 0.85 : 0.65 }}>
                                        {isPdf ? 'PDF document' : 'File attachment'}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                )}
                              </Box>
                            );
                          })() : null}
                          <Typography variant="caption" sx={{ opacity: isAgent ? 0.9 : 0.6, display: 'block', mt: 0.75 }}>
                            {formatDateTime(message.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
                {userTyping ? (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: '#ffffff',
                        border: '1px solid #E5E7EB',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        fontSize: 13,
                      }}
                    >
                      User is typing...
                    </Box>
                  </Box>
                ) : null}
                <div ref={messagesEndRef} />
              </Stack>
            </Box>

            {selectedAttachment ? (
              <Alert
                severity="info"
                sx={{ mb: 1.5 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<Close />}
                    onClick={() => setSelectedAttachment(null)}
                    disabled={submitting}
                  >
                    Remove
                  </Button>
                }
              >
                Attached: {selectedAttachment.name}
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                hidden
                onChange={handleAttachmentChange}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Reply"
                value={reply}
                onChange={handleReplyChange}
                disabled={submitting || session.status === 'closed'}
                placeholder="Type a reply to the user"
              />
              <Button
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || session.status === 'closed'}
                sx={{ minWidth: 128, height: 56 }}
              >
                Attach
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={sendReply}
                disabled={submitting || (!reply.trim() && !selectedAttachment) || session.status === 'closed'}
                sx={{
                  minWidth: 140,
                  height: 56,
                  backgroundColor: '#DC143C',
                  '&:hover': { backgroundColor: '#B91C3C' },
                }}
              >
                Send
              </Button>
            </Stack>
            {session.status === 'closed' ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Closed sessions are read-only until reactivated.
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
